import { createSlice } from '@reduxjs/toolkit';

// Helper function to recursively convert Date objects to ISO strings for Redux serialization
const sanitizeData = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof Date) return obj.toISOString();
  if (Array.isArray(obj)) return obj.map(sanitizeData);
  if (typeof obj === 'object') {
    const sanitized = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        sanitized[key] = sanitizeData(obj[key]);
      }
    }
    return sanitized;
  }
  return obj;
};

/**
 * Location-scoped `getRestaurantBySlug` responses sometimes omit media fields that exist on the
 * parent / home payload. Merging avoids wiping cover & logo when a later fetch returns blanks.
 */
const RESTAURANT_MEDIA_STRING_KEYS = [
  'coverImage', 'CoverImage', 'coverImageUrl', 'CoverImageUrl',
  'image', 'Image', 'bannerImage', 'BannerImage',
  'backgroundImage', 'BackgroundImage', 'Comp_BackgroundImage', 'Company_BackgroundImage',
  'logo', 'Logo', 'logoUrl', 'LogoUrl', 'companyLogo', 'Company_Logo', 'Comp_Logo', 'CompIcon', 'compIcon',
];

function isEmptyStringish(value) {
  return value == null || value === '' || (typeof value === 'string' && value.trim() === '');
}

function mergeRestaurantDataPreserveMedia(previous, incoming) {
  const prev = previous && typeof previous === 'object' ? previous : {};
  const inc = incoming && typeof incoming === 'object' ? incoming : {};
  const sanitizedIncoming = sanitizeData(inc);
  const merged = { ...prev, ...sanitizedIncoming };
  for (const key of RESTAURANT_MEDIA_STRING_KEYS) {
    if (isEmptyStringish(sanitizedIncoming[key]) && !isEmptyStringish(prev[key])) {
      merged[key] = prev[key];
    }
  }
  return merged;
}

/** @typedef {'idle' | 'loading' | 'succeeded' | 'failed'} RestaurantFetchStatus */

const initialState = {
  // Store restaurant data per restaurant slug: { [restaurantSlug]: restaurantData }
  restaurants: {},
  // Store company settings per restaurant slug: { [restaurantSlug]: settingsData }
  settings: {},
  // Per-slug bootstrap status: { [restaurantSlug]: { status, error } }
  fetchBySlug: {},
  // Current restaurant slug
  currentRestaurantSlug: null,
};

const restaurantSlice = createSlice({
  name: 'restaurant',
  initialState,
  reducers: {
    // Set restaurant data for a specific restaurant
    setRestaurantData: (state, action) => {
      const { restaurantSlug, restaurantData } = action.payload;
      if (restaurantSlug) {
        const previous = state.restaurants[restaurantSlug] || {};
        const merged = mergeRestaurantDataPreserveMedia(previous, restaurantData);
        merged.currency = merged.currency || restaurantData?.currency || '£';
        state.restaurants[restaurantSlug] = merged;
        state.currentRestaurantSlug = restaurantSlug;
      }
    },
    
    // Set current restaurant slug
    setCurrentRestaurantSlug: (state, action) => {
      state.currentRestaurantSlug = action.payload;
    },
    
    setRestaurantFetchStatus: (state, action) => {
      const { restaurantSlug, status, error = null } = action.payload;
      if (!restaurantSlug) return;
      state.fetchBySlug[restaurantSlug] = {
        status: status || 'idle',
        error: error || null,
      };
    },

    // Clear restaurant data
    clearRestaurantData: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (restaurantSlug && state.restaurants[restaurantSlug]) {
        delete state.restaurants[restaurantSlug];
      }
      if (restaurantSlug && state.settings[restaurantSlug]) {
        delete state.settings[restaurantSlug];
      }
      if (restaurantSlug && state.fetchBySlug[restaurantSlug]) {
        delete state.fetchBySlug[restaurantSlug];
      }
      if (state.currentRestaurantSlug === restaurantSlug) {
        state.currentRestaurantSlug = null;
      }
    },
    
    // Set company settings for a specific restaurant
    setCompanySettings: (state, action) => {
      const { restaurantSlug, settingsData } = action.payload;
      if (restaurantSlug) {
        state.settings[restaurantSlug] = sanitizeData(settingsData);
      }
    },
    
    // Clear company settings for a specific restaurant
    clearCompanySettings: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (restaurantSlug && state.settings[restaurantSlug]) {
        delete state.settings[restaurantSlug];
      }
    },
  },
});

export const { 
  setRestaurantData, 
  setCurrentRestaurantSlug, 
  clearRestaurantData,
  setCompanySettings,
  clearCompanySettings,
  setRestaurantFetchStatus,
} = restaurantSlice.actions;

// Selectors
export const selectRestaurantData = (state, restaurantSlug) => {
  return state.restaurant.restaurants[restaurantSlug] || null;
};

export const selectCurrentRestaurant = (state) => {
  const slug = state.restaurant.currentRestaurantSlug;
  return slug ? state.restaurant.restaurants[slug] || null : null;
};

export const selectCurrency = (state, restaurantSlug) => {
  const restaurant = restaurantSlug 
    ? state.restaurant.restaurants[restaurantSlug]
    : state.restaurant.restaurants[state.restaurant.currentRestaurantSlug];
  return restaurant?.currency || '£';
};

// Selector for company settings
export const selectCompanySettings = (state, restaurantSlug) => {
  return restaurantSlug 
    ? state.restaurant.settings[restaurantSlug] || null
    : state.restaurant.settings[state.restaurant.currentRestaurantSlug] || null;
};

export const selectRestaurantFetchMeta = (state, restaurantSlug) => {
  if (!restaurantSlug) return { status: 'idle', error: null };
  return state.restaurant.fetchBySlug[restaurantSlug] || { status: 'idle', error: null };
};

export const selectRestaurantBootstrapReady = (state, restaurantSlug) => {
  const data = selectRestaurantData(state, restaurantSlug);
  const meta = selectRestaurantFetchMeta(state, restaurantSlug);
  if (data) return true;
  return meta.status === 'succeeded';
};

export default restaurantSlice.reducer;
